import { c as _c } from "react/compiler-runtime";
import pixelsImage from "@/assets/png/pixels.png";
import portfolioV1Image from "@/assets/png/portfolio-v1.png";
import slothifyImage from "@/assets/png/slothify.png";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/carousel/Carousel";
import { FULL_PERCENTAGE } from "@/utils/constants";
import { cn } from "@/utils/helpers";
import Autoplay from "embla-carousel-autoplay";
import { ExternalLink } from "lucide-react";
import Image, { StaticImageData } from "next/image";
import { useEffect, useState, type MouseEvent } from "react";
const projectsCarouselContent = {
  pixels: {
    id: "pixels",
    title: "Pixels",
    description: "A Web3 staking dApp with a clean and simple user experience.",
    image: pixelsImage,
    link: "https://staking.pixels.xyz/"
  },
  "spotify-clone": {
    id: "spotify-clone",
    title: "Slothify",
    description: "A Spotify-inspired music streaming application.",
    image: slothifyImage,
    link: "https://slothify.netlify.app/"
  },
  "portfolio-v1": {
    id: "portfolio-v1",
    title: "Portfolio V1",
    description: "My first personal portfolio website.",
    image: portfolioV1Image,
    link: "https://dimitar-daskalov-v1.netlify.app/"
  },
  project: "Project"
};
const PROGRESS_UPDATE_INTERVAL = 100;
const DEFAULT_DELAY = 5000;
const PROGRESS_MAX = 100;
interface Project {
  id: string;
  title: string;
  description: string;
  image: StaticImageData;
  link: string;
}
const projects: Project[] = [projectsCarouselContent.pixels, projectsCarouselContent["spotify-clone"], projectsCarouselContent["portfolio-v1"]];
const projectsCarouselClasses = {
  card: {
    borderRadius: "rounded-[calc(1.5rem-0.125rem)]",
    base: "relative p-0 overflow-hidden"
  },
  borderContainer: "relative rounded-3xl transition-[border-color] duration-300 border-2",
  progressBar: {
    container: "absolute top-0 left-0 right-0 h-1 bg-white/10 z-20",
    fill: "h-full w-full bg-(--color-accent-3) transition-transform duration-100 ease-linear origin-left",
    borderRadius: "rounded-t-[calc(1.5rem-0.125rem)]"
  },
  image: "object-cover transition-[transform,opacity] duration-300 will-change-transform",
  overlay: "absolute inset-0 transition-opacity duration-300",
  hoverContent: "absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 z-10"
};
interface ProjectsCarouselProps {
  delayInMilliseconds?: number;
}
const resetProgress = (setProgress: (value: number) => void) => () => {
  setProgress(0);
};
export const ProjectsCarousel = t0 => {
  const $ = _c(36);
  const {
    delayInMilliseconds: t1
  } = t0;
  const delayInMilliseconds = t1 === undefined ? DEFAULT_DELAY : t1;
  const [api, setApi] = useState();
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const handleProjectClick = _temp;
  let t2;
  let t3;
  if ($[0] !== api) {
    t2 = () => {
      if (!api) {
        return;
      }
      const handleProgressReset = resetProgress(setProgress);
      api.on("select", handleProgressReset);
      api.on("reInit", handleProgressReset);
      api.on("autoplay:play", handleProgressReset);
      return () => {
        api.off("select", handleProgressReset);
        api.off("reInit", handleProgressReset);
        api.off("autoplay:play", handleProgressReset);
      };
    };
    t3 = [api];
    $[0] = api;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== api || $[4] !== delayInMilliseconds || $[5] !== isHovered) {
    t4 = () => {
      if (!api || isHovered) {
        return;
      }
      const progressIncrement = PROGRESS_MAX / (delayInMilliseconds / PROGRESS_UPDATE_INTERVAL);
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + progressIncrement;
          return newProgress >= PROGRESS_MAX ? 0 : newProgress;
        });
      }, PROGRESS_UPDATE_INTERVAL);
      return () => clearInterval(interval);
    };
    t5 = [api, delayInMilliseconds, isHovered];
    $[3] = api;
    $[4] = delayInMilliseconds;
    $[5] = isHovered;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      align: "start",
      loop: true
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== delayInMilliseconds) {
    t7 = Autoplay({
      delay: delayInMilliseconds,
      stopOnInteraction: false,
      stopOnMouseEnter: true
    });
    $[9] = delayInMilliseconds;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = [t7];
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="w-full flex justify-between items-center mb-4"><CarouselPrevious className="relative left-0 top-0 translate-x-0 translate-y-0" /><CarouselNext className="relative right-0 top-0 translate-x-0 translate-y-0" /></div>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = isHovered ? "border-white/60 glow" : "border-transparent";
  let t11;
  if ($[14] !== t10) {
    t11 = cn(projectsCarouselClasses.borderContainer, t10);
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(projectsCarouselClasses.card.base, projectsCarouselClasses.card.borderRadius);
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsHovered(true);
    t14 = () => setIsHovered(false);
    t15 = cn(projectsCarouselClasses.progressBar.container, projectsCarouselClasses.progressBar.borderRadius);
    t16 = cn(projectsCarouselClasses.progressBar.fill, projectsCarouselClasses.progressBar.borderRadius);
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
  } else {
    t13 = $[17];
    t14 = $[18];
    t15 = $[19];
    t16 = $[20];
  }
  const t17 = `scaleX(${progress / FULL_PERCENTAGE})`;
  let t18;
  if ($[21] !== t17) {
    t18 = <div className={t15}><div className={t16} style={{
        transform: t17
      }} /></div>;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== isHovered) {
    t19 = projects.map(project => <CarouselItem key={project.id} className="basis-full"><div className="relative w-full h-96 overflow-hidden"><Image src={project.image} alt={project.title} fill={true} className={cn(projectsCarouselClasses.image, isHovered && "blur-md scale-105", "backface-hidden")} sizes="(max-width: 768px) 100dvw, 80dvw" /><div className={cn(projectsCarouselClasses.overlay, isHovered && "bg-white/10")}>{isHovered && <div className={projectsCarouselClasses.hoverContent}><h2 className="heading-2 text-center text-white! text-glow">{project.title}</h2><p className="paragraph-default text-center text-white/90! max-w-2xl">{project.description}</p><Button onClick={e_0 => handleProjectClick(e_0, project.link)} variant="primary" icon={<ExternalLink className="w-5 h-5" />} className="mt-4" aria-label={`View ${project.title}`}>{projectsCarouselContent.project}</Button></div>}</div></div></CarouselItem>);
    $[23] = isHovered;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== t19) {
    t20 = <CarouselContent className="m-0 relative z-10">{t19}</CarouselContent>;
    $[25] = t19;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== t18 || $[28] !== t20) {
    t21 = <Card className={t12} hover={false} onMouseEnter={t13} onMouseLeave={t14}>{t18}{t20}</Card>;
    $[27] = t18;
    $[28] = t20;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t11 || $[31] !== t21) {
    t22 = <div className={t11}>{t21}</div>;
    $[30] = t11;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t22 || $[34] !== t8) {
    t23 = <div className="w-full max-w-6xl relative"><Carousel setApi={setApi} className="w-full" opts={t6} plugins={t8}>{t9}{t22}</Carousel></div>;
    $[33] = t22;
    $[34] = t8;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  return t23;
};
function _temp(e, link) {
  e.stopPropagation();
  window.open(link, "_blank", "noopener,noreferrer");
}